import { FC, memo, useEffect, useRef, useState } from "react";
import { OwcIcon } from "@one/react";
import { react18WorkaroundProps } from "../../../utils/constants/constants";
import { BOOKING_TYPE, EVENT, TIMELINE_TYPE } from "../../../constants";
import { getBookingDisplayText } from "../../../components/shared/helpFunctions";
import {
  getNameFromEmail,
  getUserDisplayById,
  getUserInitalsFromString,
} from "../../../utils/helpers/text";
import { AssistantBadge, BookerBadge } from "./components/badges";
import { useSelector } from "react-redux";
import getRRuleDisplayText from "../../../utils/helpers/text/transformRRuleToText";
import { getRepetitionIcon } from "../../../utils/helpers/getRepetitionIcon";
import moment, { Moment } from "moment";

interface UserData {
  userId: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}

interface BookingApprovalInfo {
  assistent?: string | string[];
  approval?: {
    status: string;
  };
}

interface BookingData {
  bookingId?: string;
  type: string;
  fcolor?: string;
  color?: string;
  reservedForEmails?: string[];
  bookingApprovalInfo?: BookingApprovalInfo;
  createdByEmail?: string;
  createdBy?: string | {
    email?: string;
    userId?: string;
  };
  description?: string;
  assistent?: string[];
  isOngoingEventFlag?: boolean;
  isFirstTimeUser?: boolean;
  repetitionPattern: string;
  dateFrom: string;
  isInformationBooking?: boolean;
  informationBookingId?: string;
}

interface EventData {
  id: string;
  start: string;
  end: string;
  startDate: Moment | string;
  endDate: Moment | string;
  title?: string;
  original: BookingData;
}

interface EditBookingData {
  id: string;
}

interface TimelineBookingEventProps {
  data: EventData;
  eventHover: string | null;
  setEventHover: (id: string | null) => void;
  usersList: UserData[];
  isPending: boolean;
  isNewBooking: boolean;
  isGoogleCalendarEvent: boolean;
  isEditMode: boolean;
  editBookingData: EditBookingData;
  isDuplicate: boolean;
  eventSlotDelete: (id: string) => void;
  manipulatePopoverContent: (params: {
    userSelectionType: string;
    eventId: string;
  }) => void;
  bookingPopUpDispatchAction: (action: { type: string; payload: any }) => void;
  handleOnClose: () => void;
  view: (typeof TIMELINE_TYPE)[keyof typeof TIMELINE_TYPE];
  renderBookingIcon: (
    type: string,
    bookingApprovalInfo?: BookingApprovalInfo,
  ) => string | undefined;
}

const getAssistent = (
  bookingApprovalInfo?: BookingApprovalInfo,
  bookingData?: BookingData,
): string => {
  if (
    bookingApprovalInfo?.assistent &&
    bookingApprovalInfo.assistent.length > 0
  ) {
    if (Array.isArray(bookingApprovalInfo.assistent)) {
      return bookingApprovalInfo.assistent[0];
    }
    return bookingApprovalInfo.assistent;
  }

  if (
    bookingData?.assistent &&
    Array.isArray(bookingData.assistent) &&
    bookingData.assistent[0]
  ) {
    return bookingData.assistent[0];
  }

  return "";
};
const COLORS = {
  PENDING_PURPLE: "#7D0096",
  PRIMARY_BLUE: "#0B41CD",
  WHITE: "#FFFFFF",
  LIGHT_BLUE_BG: "#D7E0F7",
  OFF_WHITE: "#FBFBFA",
  FIRST_TIME_BOOKING_BG: "#E4F5F1",
  EDIT_TIME_BGCOLOR: "#d7e0f7",
} as const;

// Information-slot description may visually extend past the slot's own width
// but must NOT collide with the next event in the same stack-row. We clip
// the text at (nearestSiblingLeft - currentLeft - INFO_SPILLOVER_GAP_PX).
const INFO_SPILLOVER_GAP_PX = 16;
// Tolerance (px) for treating two `.mbsc-schedule-event` siblings as being
// in the same stack-row (same vertical band). Mobiscroll vertically stacks
// overlapping events; a sibling rendered above/below the current one must
// not be considered an obstacle for the spillover calculation.
const INFO_STACK_ROW_TOLERANCE_PX = 4;

const TimelineBookingEvent: FC<TimelineBookingEventProps> = ({
  data,
  eventHover,
  setEventHover,
  usersList,
  isEditMode,
  editBookingData,
  isDuplicate,
  eventSlotDelete,
  manipulatePopoverContent,
  bookingPopUpDispatchAction,
  handleOnClose,
  renderBookingIcon,
  view,
}) => {
  const bookingId = data.original?.bookingId;
  const type = data.original.type;
  const storeUser = useSelector((store: { user: { timezone: string } }) => store.user);
  const displayTimeZone = storeUser.timezone || "UTC";
  // When the booking wizard is forced into Information-only mode (overlap
  // detected between admin's pending slots and an existing booking, or
  // between two pending slots), pending slots ("available_*") are visually
  // demoted to 18px height and pushed to the top of the row. Reading the
  // flag directly here (instead of tagging it onto the event data) keeps
  // the event data array reference stable, so Mobiscroll does not snap
  // mid-drag slots back to their previous position when the flag toggles.
  const forceInformationOnly = useSelector(
    (store: { reservations?: { forceInformationOnly?: boolean } }) =>
      !!store?.reservations?.forceInformationOnly,
  );

  const { start, end } = data;

  const isPending =
    data.original?.bookingApprovalInfo?.approval?.status === "PENDING";
  const isNewBooking =
    (data.original.type === BOOKING_TYPE.AVAILABLE ||
      !data.original?.bookingId) &&
    data.original.type !== "GoogleCalendar";
  const isGoogleCalendarEvent = data.original.type === "GoogleCalendar";
  const isFirstTimeBooking = !!data.original?.isFirstTimeUser;
  const isPendingSlot =
    typeof data.id === "string" && data.id.startsWith("available_");
  // Treat a real Information booking and a forced-Information pending slot
  // identically for layout purposes (18px height, hide chrome, centered).
  const isInformationBooking =
    !!data.original?.isInformationBooking ||
    (isPendingSlot && forceInformationOnly);
  const recurranceText = getRRuleDisplayText(data.original.repetitionPattern, new Date(data.original.dateFrom), displayTimeZone);

  const isOriginalBookingSlot = isEditMode && !isDuplicate &&
    bookingId === editBookingData.id &&
    !data.original.bookingId?.startsWith("available_");

  const fontColor = isFirstTimeBooking
    ? COLORS.PENDING_PURPLE
    : isPending
      ? COLORS.PENDING_PURPLE
      : (data.original.fcolor ?? COLORS.PRIMARY_BLUE);

  const backgroundColor = isFirstTimeBooking
    ? COLORS.FIRST_TIME_BOOKING_BG
    : isPending
      ? COLORS.WHITE
      : isOriginalBookingSlot ? COLORS.EDIT_TIME_BGCOLOR
        : (data.original.color ?? COLORS.LIGHT_BLUE_BG);

  const borderColor = isFirstTimeBooking
    ? COLORS.WHITE
    : isPending
      ? COLORS.PENDING_PURPLE
      : isEditMode
        ? (bookingId === editBookingData.id || bookingId === null) &&
        (data?.original?.fcolor ?? COLORS.PRIMARY_BLUE)
        : (type === BOOKING_TYPE.AVAILABLE && COLORS.OFF_WHITE) || "#fff";

  const reservedForEmails = data.original?.reservedForEmails || [];

  const assistantId = getAssistent(
    data?.original?.bookingApprovalInfo,
    data?.original,
  );
  const assistantDisplay = getUserDisplayById(assistantId, usersList);
  const assistantInitials =
    assistantDisplay.length > 1
      ? getUserInitalsFromString(assistantDisplay)
      : "";

  const allUsers = [...reservedForEmails];
  const uniqueUsers = [...new Set(allUsers)].filter(Boolean);
  const mainUser = uniqueUsers[0];
  const userInitials =
    mainUser?.length > 1 ? getUserInitalsFromString(mainUser) : "-";

  const isDayOr3DayView =
    view === TIMELINE_TYPE.DAY || view === TIMELINE_TYPE["3DAY"];

  // Must match BookerBadge's own avatar (mainUser/reservedFor), not the creator -
  // otherwise the label shows the wrong person when an admin books for someone else.
  const bookerName = isInformationBooking
    ? (data.original?.description || "-")
    : (uniqueUsers.length > 1
      ? uniqueUsers.map((user) => getNameFromEmail(user)).join(", ")
      : getNameFromEmail(mainUser));

  // BOOK_LOG-7469053: For the tooltip's "Info from:" line we want IDENTICAL
  // formatting to a regular booking's "Run by:" line, which is
  // `getNameFromEmail(createdByEmail)`. Information bookings have an empty
  // `createdByEmail` on the BE payload, so we resolve the UUID in `createdBy`
  // to an email via the cached usersList and feed it to the same helper -
  // no custom formatting, no fallbacks, exact parity with the regular line.
  const informationCreatedByEmail = isInformationBooking
    ? usersList?.find((u) => u.userId === data.original?.createdBy)?.email
    : undefined;
  const informationCreatedByName = isInformationBooking
    ? getNameFromEmail(informationCreatedByEmail)
    : "";
  const repetitionIconStyle = { cursor: "pointer" } as const;

  const openEventPopover = () => {
    if (bookingId && !isGoogleCalendarEvent) {
      manipulatePopoverContent({
        userSelectionType: EVENT,
        eventId: data.id,
      });
    }
  };

  const formatTimeRange = (start: string, end: string) => {
    return `${start} - ${end}`;
  };

  const formatTooltipTimeRange = (startDate: Moment | string, endDate: Moment | string) => {
    const timezone = user?.timezone;
    moment.isMoment(startDate) ? startDate = startDate.format("DD-MMM-YYYY h:mmA") : startDate = moment.tz(new Date(startDate), timezone).format("DD-MMM-YYYY h:mmA");
    moment.isMoment(endDate) ? endDate = endDate.format("DD-MMM-YYYY h:mmA") : endDate = moment.tz(new Date(endDate), timezone).format("DD-MMM-YYYY h:mmA");
    return `${startDate} - ${endDate}`;
  };

  const eventRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 16, y: 52 });
  const [hasEnoughWidth, setHasEnoughWidth] = useState(false);
  const [shouldAllowSpillover, setShouldAllowSpillover] = useState(true);
  // Information-slot spillover. Holds the maximum width (px) the description
  // text may occupy starting from the slot's left edge, clipped at the
  // nearest sibling event in the same stack-row minus INFO_SPILLOVER_GAP_PX.
  // `null` means "no constraint yet" (initial render / no row found).
  const [informationMaxWidth, setInformationMaxWidth] = useState<number | null>(
    null,
  );
  const [stickyOffset, setStickyOffset] = useState(0);
  const { isTvMode, user } = useSelector(
    (state: any) => ({ isTvMode: state?.bookingInstruments?.isTvMode, user: state?.user }),
  );
  useEffect(() => {
    const element = eventRef.current?.parentElement;

    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        setHasEnoughWidth(width >= 36);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const element = eventRef.current?.parentElement;
    if (!element || isNewBooking) return;

    const calculateSpillover = () => {
      const timelineRow = element.closest(".mbsc-timeline-row");
      if (!timelineRow) {
        return;
      }

      const allBookingsInRow = timelineRow.querySelectorAll(
        ".mbsc-schedule-event",
      );
      const currentRect = element.getBoundingClientRect();
      const currentLeft = currentRect.left;
      const currentTop = currentRect.top;
      const currentWidth = currentRect.width;
      if (currentWidth >= 189) {
        setShouldAllowSpillover(false);
      }
      const minSpilloverDistance = Math.max(currentWidth, 189);

      let hasEnoughSpace = true;
      let minDistance = Infinity;
      // Independent tracker for Information-slot clipping: closest sibling
      // in the SAME stack-row (vertical-top within tolerance). The generic
      // `minDistance` above counts any sibling regardless of vertical
      // position, which is fine for the binary spillover toggle but would
      // over-clip Information text when an unrelated booking is stacked
      // above/below in the same row.
      let infoMinDistanceInSameStackRow = Infinity;

      allBookingsInRow.forEach((siblingBooking) => {
        if (siblingBooking === element) return;

        const siblingRect = siblingBooking.getBoundingClientRect();
        const siblingLeft = siblingRect.left;
        const distanceToSibling = siblingLeft - currentLeft;

        if (distanceToSibling > 0 && distanceToSibling < minDistance) {
          minDistance = distanceToSibling;
        }

        if (distanceToSibling > 0 && distanceToSibling < minSpilloverDistance) {
          hasEnoughSpace = false;
        }

        if (
          distanceToSibling > 0 &&
          Math.abs(siblingRect.top - currentTop) <=
            INFO_STACK_ROW_TOLERANCE_PX &&
          distanceToSibling < infoMinDistanceInSameStackRow
        ) {
          infoMinDistanceInSameStackRow = distanceToSibling;
        }
      });

      setShouldAllowSpillover(hasEnoughSpace);

      // For Information slots: bound the description text at the nearest
      // sibling in the same stack-row (minus gap) so the spillover never
      // visually collides with adjacent slots or bookings. When no sibling
      // is found to the right, fall back to the timeline-row width so the
      // text can extend up to the row's right edge.
      let infoMaxWidth: number;
      if (Number.isFinite(infoMinDistanceInSameStackRow)) {
        infoMaxWidth = Math.max(
          currentWidth,
          infoMinDistanceInSameStackRow - INFO_SPILLOVER_GAP_PX,
        );
      } else {
        const rowRect = (timelineRow as HTMLElement).getBoundingClientRect();
        infoMaxWidth = Math.max(currentWidth, rowRect.right - currentLeft);
      }
      setInformationMaxWidth(infoMaxWidth);
    };

    calculateSpillover();

    window.addEventListener("resize", calculateSpillover);

    const timelineRow = element.closest(".mbsc-timeline-row");
    if (timelineRow) {
      // Throttle spillover recomputation to one call per animation frame and
      // observe only direct children (not the whole subtree). During a
      // drag/resize Mobiscroll mutates row contents on every mouse move; a
      // subtree observer would re-run the O(siblings) layout-thrashing
      // calculation synchronously for every single mutation, which is the
      // main cause of laggy drag in timelines with many rows.
      let rafId: number | null = null;
      const scheduleRecalc = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          calculateSpillover();
        });
      };
      const observer = new MutationObserver(scheduleRecalc);
      observer.observe(timelineRow, {
        childList: true,
        subtree: false,
      });

      return () => {
        window.removeEventListener("resize", calculateSpillover);
        observer.disconnect();
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }

    return () => {
      window.removeEventListener("resize", calculateSpillover);
    };
  }, [data.start, data.end, data.id, bookingId, isNewBooking]);

  useEffect(() => {
    const element = eventRef.current?.parentElement;
    if (!element || isNewBooking) return;
    // Information bookings (18px height) keep their text statically anchored
    // inside the slot -- no sticky-follow under the resource sidebar. Returning
    // early here also ensures stickyOffset stays at 0 if a slot becomes an
    // Information booking after mount.
    if (isInformationBooking) {
      setStickyOffset(0);
      return;
    }

    const scrollContainer = element.closest(".mbsc-timeline-grid-scroll");
    if (!scrollContainer) return;

    const timeline = element.closest(".mbsc-timeline");
    const resourceSidebar = timeline?.querySelector(".mbsc-timeline-resources");
    const resourceWidth = resourceSidebar
      ? (resourceSidebar as HTMLElement).clientWidth
      : 0;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const bookingRect = element.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        const bookingLeftRelativeToContainer =
          bookingRect.left - containerRect.left;
        const bookingWidth = bookingRect.width;

        if (bookingLeftRelativeToContainer < resourceWidth) {
          const scrolledUnderSidebar =
            resourceWidth - bookingLeftRelativeToContainer;
          const minContentWidth =
            shouldAllowSpillover && bookingWidth < 183 ? 0 : 183;
          const maxOffset = Math.max(0, bookingWidth - 36 - minContentWidth);
          const offset = Math.min(scrolledUnderSidebar, maxOffset);
          setStickyOffset(offset);
        } else {
          setStickyOffset(0);
        }
      });
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isNewBooking, isInformationBooking, shouldAllowSpillover]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (eventHover === data.id) {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = 10;

      const element = eventRef.current?.parentElement;
      const scrollContainer = element?.closest(
        ".mbsc-timeline-grid-scroll",
      ) as HTMLElement;

      const tooltipHeight =
        tooltipRef.current?.offsetHeight ?? 150;
      const eventHeight = rect.height;

      let showOnTop = false;
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const spaceBelow = containerRect.bottom - rect.bottom;
        showOnTop = spaceBelow < tooltipHeight;
      }

      setTooltipPosition({
        x: e.clientX - rect.left + offsetX,
        y: showOnTop
          ? -(tooltipHeight - eventHeight) - 46
          : eventHeight + 16,
      });
    }
  };

  const renderDayOr3DayViewContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "36px",
        justifyContent: assistantDisplay && !isInformationBooking ? "flex-start" : "center",
        alignItems: assistantDisplay && !isInformationBooking ? "flex-start" : "center",
        width: "100%",
      }}
    >
      <div
        style={{
          borderRadius: "0.25rem",
          display: "flex",
          alignItems: "center",
          height: "18px",
        }}
      >
        <span
          style={{
            display: "flex",
            gap: "4px",
            textOverflow: "ellipsis",
            fontFamily: "Roche Sans",
            fontSize: "0.75rem",
            fontStyle: "normal",
            fontWeight: "400",
            whiteSpace: "nowrap",
            cursor: 'pointer'
          }}
        >
          {!isInformationBooking && recurranceText && (
            <>
              {getRepetitionIcon(recurranceText, repetitionIconStyle)}
            </>
          )}
          {bookerName}
        </span>
      </div>
      {!isInformationBooking && assistantDisplay && (
        <div
          style={{
            borderRadius: "0.25rem",
            maxWidth: "100%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            height: "18px",
          }}
        >
          <span
            style={{
              color: "#7D0096",
              textOverflow: "ellipsis",
              fontFamily: "Roche Sans",
              fontSize: "0.75rem",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            {assistantDisplay}
          </span>
        </div>
      )}
    </div>
  );

  const renderNewBookingContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "36px",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div
        style={{
          borderRadius: "0.25rem",
          display: "flex",
          alignItems: "center",
          height: "18px",
        }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            fontFamily: "Roche Sans",
            fontSize: "0.75rem",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "18px",
            whiteSpace: "nowrap",
          }}
        >
          {formatTimeRange(start, end)}
        </span>
      </div>
    </div>
  );

  const renderDefaultContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "36px",
        alignItems: 'center',
        gap: '4px',
        width: "100%",
      }}
    >
      {!isInformationBooking && recurranceText && (
        <>
          {getRepetitionIcon(recurranceText, repetitionIconStyle)}
        </>
      )}
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: isInformationBooking ? "center" : "flex-start",
        alignItems: "flex-start",
        height: "100%",
      }}>
        <div
          style={{
            borderRadius: "0.25rem",
            display: "flex",
            alignItems: "center",
            height: "18px",
            maxWidth: "100%",
          }}
        >
          <span
            style={{
              textOverflow: "ellipsis",
              fontFamily: "Roche Sans",
              fontSize: "0.75rem",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            {bookerName}
          </span>
        </div>
        {!isInformationBooking && (
          <div
            style={{
              borderRadius: "0.25rem",
              display: "flex",
              alignItems: "center",
              height: "18px",
            }}
          >
            <span
              style={{
                textOverflow: "ellipsis",
                fontFamily: "Roche Sans",
                fontSize: "0.75rem",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "18px",
                whiteSpace: "nowrap",
              }}
            >
              {formatTimeRange(start, end)}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingContent = () => {
    if (isDayOr3DayView && !isNewBooking) {
      return renderDayOr3DayViewContent();
    }
    if (isNewBooking) {
      return renderNewBookingContent();
    }
    return renderDefaultContent();
  };

  return (
    <div
      ref={eventRef}
      className={`md-timeline-template-event ${isPending ? "pending-booking" : ""} ${isGoogleCalendarEvent ? "google-calendar-event" : ""} ${isNewBooking ? "new-booking" : ""} ${isInformationBooking ? "information-booking" : ""}`}
      style={{
        boxSizing: "content-box",
        cursor: 'pointer',
        minWidth: "auto",
      }}
      onMouseEnter={() => setEventHover(data.id)}
      onMouseLeave={() => setEventHover(null)}
      onMouseMove={handleMouseMove}
    >
      {!isGoogleCalendarEvent && (
        <div
          ref={tooltipRef}
          className="md-timeline-tooltip-single"
          style={{
            position: "absolute",
            top: `${tooltipPosition.y}px`,
            left: `${tooltipPosition.x}px`,
            height: "fit-content",
            width: "max-content",
            borderRadius: "4px",
            backgroundColor: "#F5F5F2",
            padding: "12px",
            gap: "4px",
            display: !isNewBooking && eventHover === data.id ? "flex" : "none",
            flexDirection: "row",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              zIndex: 999999,
              position: "relative",
              borderRadius: "4px",
              border: "1px solid #FBFBFA",
              backgroundColor: backgroundColor,
              marginTop: '3px'
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "5px",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                position: "absolute",
                top: "1px",
              }}
            ></div>

            <OwcIcon
              className="one-icons-outlined"
              style={{
                fontSize: "16px",
                width: "24px",
                height: "24px",
                lineHeight: "24px",
                textAlign: "center",
                color: fontColor,
                border: borderColor,
              }}
              key={data.id}
              name={
                renderBookingIcon(
                  type,
                  data?.original?.bookingApprovalInfo,
                ) as any
              }
              {...react18WorkaroundProps}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              color: "rgba(33, 32, 31, 1)",
            }}
          >
            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {isInformationBooking
                  ? "Info from: "
                  : `${getBookingDisplayText(data?.original?.bookingApprovalInfo)} by: `}
              </span>
              {isInformationBooking
                ? informationCreatedByName
                : (uniqueUsers.length > 1
                  ? uniqueUsers.map((user) => getNameFromEmail(user)).join(", ")
                  : getNameFromEmail(uniqueUsers[0]))}
            </span>

            {!isInformationBooking && (
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {"Booking for: "}
                </span>
                {uniqueUsers.length > 1
                  ? uniqueUsers.map((user) => getNameFromEmail(user)).join(", ")
                  : getNameFromEmail(uniqueUsers[0])}
              </span>
            )}

            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {"Duration: "}
              </span>
              {formatTooltipTimeRange(data.startDate, data.endDate)}
            </span>

            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {"Description: "}
              </span>
              {data.original?.description || "-"}
            </span>

            {isInformationBooking && (
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>{"Project: "}</span>
                {(() => {
                  const rp = data.original?.redPandaProjects;
                  if (Array.isArray(rp) && rp.length > 0) {
                    return rp
                      .map((p: any) =>
                        p?.themeNumber && (p?.projectShortName || p?.projectName)
                          ? `${p.themeNumber} - ${p.projectShortName || p.projectName}`
                          : p?.projectShortName || p?.projectName || p?.themeNumber || ""
                      )
                      .filter(Boolean)
                      .join(", ") || "-";
                  }
                  return (data.original as any)?.project || "-";
                })()}
              </span>
            )}

            {isInformationBooking && (
              <span
                style={{
                  fontSize: "12px",
                  marginTop: "8px",
                }}
              >
                {"Informational booking type does not hinder you from booking"}
              </span>
            )}

            {assistantInitials && (
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {"Assistant: "}
                </span>
                {assistantDisplay}
              </span>
            )}
          </div>
        </div>
      )}

      <div
        className="md-timeline-template-event-cont"
        style={{
          borderColor: borderColor || "#fff",
          background: backgroundColor,
          boxShadow: isPending
            ? "0px 2px 4px 0px rgba(124, 58, 237, 0.25)"
            : "0px 1px 3px 0px rgba(0, 0, 0, 0.40)",
          borderStyle: "solid",
          borderWidth: "1px",
          // Information slots always need overflow:visible on this container
          // so the inner `.booking-title-container` can extend past the
          // slot's own width. The booking-title-container itself enforces
          // the hard clip (overflow:hidden + dynamic maxWidth) so the
          // spillover never collides with the next event in the row.
          overflow:
            isInformationBooking || shouldAllowSpillover ? "visible" : "hidden",
          boxSizing: "content-box",
          width: "auto",
          minWidth: "inherit",
        }}
        onClick={openEventPopover}
      >
        {isGoogleCalendarEvent ? (
          <>
            <span
              className="md-timeline-template-title"
              style={{
                color: fontColor,
                fontWeight: "bold",
                marginLeft: "4px",
                display: "block",
              }}
            >
              {data.title}
            </span>
            <span
              className="md-timeline-template-time"
              style={{ color: fontColor }}
            >
              {formatTimeRange(start, end)}
            </span>
          </>
        ) : (
          <>
            {!isNewBooking && !isInformationBooking && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "36px",
                  width: "100%",
                  background: backgroundColor,
                  borderRadius: "7px",
                }}
              />
            )}
            {!isNewBooking && (
              <div
                className="md-timeline-template-event-type"
                style={{
                  overflow: "hidden",
                  position: "absolute",
                  left: `${stickyOffset}px`,
                  top: 0,
                  height: isInformationBooking ? "18px" : "36px",
                  width: "100%",
                  maxWidth: isInformationBooking ? "18px" : "36px",
                }}
              >
                <div
                  style={{
                    minWidth: "12px",
                    height: isInformationBooking ? "18px" : "36px",
                    width: "100%",
                    maxWidth: isInformationBooking ? "18px" : "36px",
                    boxSizing: "border-box",
                    position: "relative",
                    borderRadius: "7px",
                    borderRight: hasEnoughWidth
                      ? `1px solid ${borderColor}`
                      : "none",
                  }}
                >
                  <OwcIcon
                    className="one-icons-outlined"
                    style={{
                      position: "absolute",
                      top: isInformationBooking ? "1px" : "5px",
                      fontSize: isInformationBooking ? "16px" : "20px",
                      left: isInformationBooking ? "1px" : "8px",
                      width: isInformationBooking ? "16px" : "20px",
                      height: isInformationBooking ? "16px" : "20px",
                      color: fontColor,
                      border: borderColor,
                      cursor: "pointer"
                    }}
                    key={data.id}
                    name={
                      (eventHover &&
                        data.id === eventHover &&
                        (isEditMode
                          ? bookingId === editBookingData.id || bookingId === null
                          : bookingId === null) &&
                        !(isDuplicate && !data.id?.includes("available_"))
                        ? "clear"
                        : renderBookingIcon(
                          type,
                          data?.original?.bookingApprovalInfo,
                        )) as any
                    }
                    onClick={(ev) => {
                      if (isEditMode) {
                        if (data.original.isOngoingEventFlag) {
                          bookingPopUpDispatchAction({
                            type: "cancelEvents",
                            payload: {
                              onApprove: handleOnClose,
                            },
                          });
                        } else {
                          if (
                            bookingId === editBookingData.id ||
                            bookingId === null
                          ) {
                            if (
                              !(isDuplicate && !data.id?.includes("available_"))
                            ) {
                              eventSlotDelete(data.id);
                            }
                          }
                        }
                      } else {
                        if (bookingId === null) {
                          eventSlotDelete(data.id);
                        }else if (bookingId && !isGoogleCalendarEvent) {
                          openEventPopover();
                        }
                      }
                      ev.stopPropagation();
                    }}
                    {...react18WorkaroundProps}
                  />
                  {assistantInitials && !isInformationBooking && (
                    <AssistantBadge
                      initials={assistantInitials}
                      displayName={assistantDisplay}
                      isPending={isPending}
                    />
                  )}

                  {mainUser && !isInformationBooking && (
                    <BookerBadge
                      initials={userInitials}
                      hasMultipleUsers={uniqueUsers.length > 1}
                      isPending={isPending}
                      fontColor={fontColor}
                      isFirstTimeBooking={
                        data.original?.isFirstTimeUser || false
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {isNewBooking && (
              <OwcIcon
                className="one-icons-outlined"
                style={{
                  position: "absolute",
                  fontSize: "20px",
                  left: "8px",
                  width: "20px",
                  height: "20px",
                  color: fontColor,
                  border: borderColor,
                  cursor: "pointer"
                }}
                key={data.id}
                name={
                  (eventHover &&
                    data.id === eventHover &&
                    (isEditMode
                      ? bookingId === editBookingData.id || bookingId === null
                      : bookingId === null)
                    ? "clear"
                    : renderBookingIcon(
                      type,
                      data?.original?.bookingApprovalInfo,
                    )) as any
                }
                onClick={(ev) => {
                  if (isEditMode) {
                    if (data.original.isOngoingEventFlag) {
                      bookingPopUpDispatchAction({
                        type: "cancelEvents",
                        payload: {
                          onApprove: handleOnClose,
                        },
                      });
                    } else {
                      if (
                        bookingId === editBookingData.id ||
                        bookingId === null
                      ) {
                        eventSlotDelete(data.id);
                      }
                    }
                     ev.stopPropagation();
                  } else {
                    if (bookingId === null) {
                      eventSlotDelete(data.id);
                    }
                  }
                }}
                {...react18WorkaroundProps}
              />
            )}

            <div
              className={`booking-title-container ${isTvMode ? "booking-title-container--tv-mode" : ""}`}
              style={{
                position: "absolute",
                left: `${(isInformationBooking ? 18 : 36) + stickyOffset}px`,
                // Information slots: bound the text width to the available
                // space up to the next event in the same stack-row (computed
                // in `calculateSpillover`). The container is hard-clipped
                // with overflow:hidden + ellipsis so spilling text never
                // visually collides with adjacent slots/bookings. Clicks on
                // the spillover area pass through (pointer-events: none).
                ...(isInformationBooking
                  ? {
                      maxWidth:
                        typeof informationMaxWidth === "number"
                          ? `${Math.max(0, informationMaxWidth - (18 + stickyOffset))}px`
                          : undefined,
                      overflow: "hidden",
                      pointerEvents: "none" as const,
                    }
                  : {}),
                marginLeft: "4px",
                color: fontColor,
                whiteSpace: "nowrap",
                ...(isInformationBooking
                  ? {}
                  : { overflow: shouldAllowSpillover ? "visible" : "hidden" }),
                direction: "ltr",
                textOverflow: "ellipsis",
                background: "none",
              }}
            >
              {renderBookingContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(TimelineBookingEvent, (prev, next) => {
  // Mobiscroll Eventcalendar internally bumps its own "update" counter
  // roughly 16 times per second (a force-refresh loop in v5.35 that we
  // cannot disable). Without memoization this re-runs this expensive
  // renderer (3 useSelectors + MutationObserver + scroll listener +
  // getBoundingClientRect over siblings) ~16x/s per visible event in idle,
  // and dozens of times per second during drag/resize.
  //
  // We compare only the props that actually change what we render. Unstable
  // callback identities (eventSlotDelete, manipulatePopoverContent, ...) are
  // intentionally ignored here: they read up-to-date state via the latest
  // closure captured by the parent, and including them would defeat the
  // memo because the parent recreates them on every render.
  const prevData = prev.data;
  const nextData = next.data;
  if (prevData === nextData && prev.eventHover === next.eventHover &&
      prev.isEditMode === next.isEditMode &&
      prev.isDuplicate === next.isDuplicate &&
      prev.view === next.view &&
      prev.editBookingData?.id === next.editBookingData?.id &&
      prev.usersList === next.usersList) {
    return true;
  }
  if (
    prev.eventHover !== next.eventHover ||
    prev.isEditMode !== next.isEditMode ||
    prev.isDuplicate !== next.isDuplicate ||
    prev.view !== next.view ||
    prev.editBookingData?.id !== next.editBookingData?.id ||
    prev.usersList !== next.usersList
  ) {
    return false;
  }
  // Fall back to comparing the meaningful fields of `data` when its
  // reference changed but the visible content did not. Mobiscroll
  // sometimes recreates event wrappers without mutating the underlying
  // booking, and that should not force a re-render.
  if (
    prevData?.id !== nextData?.id ||
    prevData?.start !== nextData?.start ||
    prevData?.end !== nextData?.end ||
    prevData?.title !== nextData?.title ||
    prevData?.original !== nextData?.original
  ) {
    return false;
  }
  return true;
});
